import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/feature/AnimeCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getAnimeById, animeList, categoryInfo } from '@/mocks/animeData';

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = getAnimeById(Number(id));

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-foreground-300 text-3xl" />
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">未找到该动漫</h1>
          <p className="text-foreground-500 text-sm mb-6">该动漫作品可能已被移除或链接无效</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line" /> 返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const cat = categoryInfo[anime.category];
  const relatedAnime = animeList
    .filter(a => a.category === anime.category && a.id !== anime.id)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${anime.title} - 51动漫在线观看`,
    description: anime.description,
    author: { '@type': 'Person', name: anime.author },
    datePublished: '2025-01-01',
    dateModified: anime.updatedAt,
    image: anime.cover,
    publisher: {
      '@type': 'Organization',
      name: '51动漫',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51anime.com' },
        { '@type': 'ListItem', position: 2, name: cat.name, item: `https://51anime.com/category/${cat.slug}` },
        { '@type': 'ListItem', position: 3, name: anime.title },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-24 pb-16 md:pt-28 md:pb-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: cat.name, to: `/category/${cat.slug}` },
              { label: anime.title },
            ]} />

            {/* Detail Header */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-10 mt-4">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={anime.cover}
                    alt={`${anime.title} - 51动漫`}
                    title={`${anime.title} - 51动漫免费全集在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <Link
                  to={`/category/${cat.slug}`}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg" /> 开始阅读
                </Link>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    anime.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {anime.status}
                  </span>
                  <span className="text-foreground-400 text-xs">{anime.category === 'korean' ? '韩漫' : anime.category === 'japanese' ? '日漫' : '国漫'}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">{anime.title}</h1>

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-4 mb-5 text-sm">
                  <div className="flex items-center gap-1">
                    <i className="ri-user-line text-foreground-400" />
                    <span className="text-foreground-600">{anime.author}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500" />
                    <span className="text-foreground-900 font-medium">{anime.rating}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-line text-foreground-400" />
                    <span className="text-foreground-600">{anime.views} 阅读</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-file-list-3-line text-foreground-400" />
                    <span className="text-foreground-600">共 {anime.chapters} 话</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-time-line text-foreground-400" />
                    <span className="text-foreground-600">更新于 {anime.updatedAt}</span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div className="bg-background-100 rounded-lg p-5 mb-5">
                  <h4 className="font-semibold text-foreground-900 text-sm mb-2">作品简介</h4>
                  <p className="text-foreground-600 text-sm leading-relaxed">{anime.description}</p>
                </div>

                {/* Latest Chapter */}
                <div className="flex items-center gap-3 bg-accent-50 border border-accent-200/50 rounded-lg p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-book-open-line text-accent-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground-500">最新更新</p>
                    <p className="text-sm font-medium text-foreground-900 truncate">{anime.latestChapter}</p>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{anime.updatedAt}</span>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Anime */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-1">相关推荐</h2>
                <p className="text-foreground-500 text-sm">更多{cat.name}推荐</p>
              </div>
              <Link to={`/category/${cat.slug}`} className="text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors whitespace-nowrap">
                查看更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {relatedAnime.map((a) => (
                <AnimeCard key={a.id} anime={a} />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}